import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";

import API from "utils/api";
import { useUser } from "utils/hooks";

function sortConversations(a, b) {

  // Unread always first
  if (a.unread && !b.unread) {
    return -1;
  } else if (b.unread && !a.unread) {
    return 1;
  }

  // Next date
  if (a.updatedAt > b.updatedAt) {
    return -1;
  } else {
    return 1;
  }

  return 0;
}

export default function useConversations(userId) {
  const history = useHistory();
  const { token } = useUser();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(0);
  const [page, setPage] = useState(0);

  // Fetch conversations
  async function fetchConversations() {
    setLoading(true);
    const { response, data } = await API.fetchConversations(token, userId, { page });

    if (response.ok) {
      setConversations((data?.results ?? []).sort(sortConversations));
      setCount(data?.count ?? 0);
      setLimit(data?.limit ?? 0);
      setPage(data?.page ?? 0);
    }

    setLoading(false);
  }

  // Create new conversation
  async function createConversation(formValues, { success }) {
    const { response, data } = await API.createConversation(token, formValues.user, formValues);

    if (response.ok) {
      success();
      history.push(`/messages/${data?.id}`);
      return { success: true };
    }

    return {
      success: false,
      data,
    };
  }

  // Append new conversation
  const appendConversation = useCallback((conversation) => {
    setConversations(current => [...current, conversation].sort(sortConversations));
  }, []);

  // Mark a conversation as read
  const markConversationRead = useCallback((conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId) ?? null;

    // Not found, ignore
    if (conversation === null || conversation.unread === false) {
      return;
    }

    // Mark conversation as read, it's updated when fetching in the backend.
    const otherConversations = conversations.filter(c => c.id !== conversation.id);

    const updatedConversations = [
      ...otherConversations,
      { ...conversation, unread: false }
    ].sort(sortConversations);

    setConversations(updatedConversations);
  }, [conversations]);

  useEffect(() => {
    fetchConversations();
  }, [token, userId, page]);

  return {
    loading,
    conversations,
    count,
    limit,
    page,
    read: markConversationRead,
    create: createConversation,
    append: appendConversation,
    setPage,
  };
}
